import React, { useState } from "react";
import _ from "lodash";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useApp } from "../../design/AppContext";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import { MdWarningAmber } from "react-icons/md";
import { Banner, Field, PasswordField } from "../mui";

/**
 * 變更密碼表單（目前的密碼／新密碼／確認新密碼）。
 * 「設定 → 我的帳號」右半邊與 /Me/Password 頁共用這一份，規則與錯誤說明只維護一處。
 *
 * onSuccess：密碼變更成功後呼叫（表單會先清空）；onCancel 有給才畫「取消」。
 * idPrefix：同一頁放兩份時避免 id 撞名。
 */

/**
 * 與後端 AuthService.ValidateNewPassword 同一套規則；這裡只是即時提示，
 * 真正算數的在後端。Setting/AdminEdit.js 的 checkPassword 是同一套，改規則要一起改。
 */
export function checkPassword(pwd, account) {
  if (!pwd) return "請輸入新密碼";
  if (pwd.length < 8) return "長度至少需 8 個字元";
  if (!pwd.trim()) return "不可僅包含空白";
  if (/^\d+$/.test(pwd)) return "不可全為數字";
  if (account && pwd.toLowerCase().includes(String(account).toLowerCase())) return "不可包含帳號";
  return "";
}

function PasswordChangeForm({ PUT_Password, onSuccess, onCancel, idPrefix }) {
  const navigate = useNavigate();
  const { toast, userInfo } = useApp();
  const account = _.get(userInfo, "account", _.get(userInfo, "Account", ""));
  const id = (k) => `${idPrefix}-${k}`;

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  /*
   * 錯誤分成兩層：欄位自己的問題（規則沒過、兩次不一樣）寫在該欄位下面；
   * 送出之後被後端擋下來（目前的密碼不對）寫成整條 Banner —— 那是「密碼沒有被改掉」這個後果。
   */
  const [banner, setBanner] = useState("");
  const [touched, setTouched] = useState(false);

  const nextBad = next ? checkPassword(next, account) : "";
  const mismatch = touched && confirm !== "" && next !== confirm;
  const sameAsOld = touched && next !== "" && next === current;

  const submit = (e) => {
    e.preventDefault();
    if (busy) return;
    setTouched(true);

    if (!current) {
      setBanner("請輸入目前的密碼，以確認您的身分。");
      return;
    }
    const bad = checkPassword(next, account);
    if (bad) {
      setBanner(`新密碼${bad}。`);
      return;
    }
    if (next !== confirm) {
      setBanner("兩次輸入的新密碼不一致，請重新確認。");
      return;
    }
    if (next === current) {
      setBanner("新密碼不可與目前的密碼相同，請設定其他密碼。");
      return;
    }

    setBanner("");
    PUT_Password(
      { CurrentPassword: current, NewPassword: next },
      () => {
        toast("密碼已變更");
        setCurrent("");
        setNext("");
        setConfirm("");
        setTouched(false);
        onSuccess();
      },
      setBusy,
      (msg) => setBanner(msg || "密碼變更失敗，請再試一次。")
    );
  };

  return (
    <Stack component="form" onSubmit={submit} spacing={2} noValidate>
      {banner ? (
        <Banner kind="attention" role="alert" icon={<MdWarningAmber />}>
          {banner}
        </Banner>
      ) : null}

      <Field
        label="目前的密碼"
        htmlFor={id("old")}
        mark="req"
        footer={
          /* 想不起來的人要有一條出路 */
          <Box>
            <Button variant="text" type="button" onClick={() => navigate("/Auth/ForgotPassword")} sx={{ px: 0 }}>
              忘記目前的密碼？
            </Button>
          </Box>
        }
      >
        <PasswordField
          placeholder="請輸入目前的密碼"
          id={id("old")}
          fullWidth
          value={current}
          autoComplete="current-password"
          onChange={(e) => setCurrent(e.target.value)}
        />
      </Field>

      {/* 規則先講；打的內容過不了就換成錯誤說明的整句話，不是只把框框變紅 */}
      <Field
        label="新密碼"
        htmlFor={id("new")}
        mark="req"
        help={nextBad ? null : "至少 8 個字元，不可全為數字，亦不可包含您的帳號。"}
        helpId={id("new-help")}
        error={nextBad ? `新密碼${nextBad}。` : sameAsOld ? "新密碼不可與目前的密碼相同。" : null}
        errorId={id("new-err")}
      >
        <PasswordField
          placeholder="至少 8 個字元"
          id={id("new")}
          fullWidth
          error={!!nextBad || sameAsOld}
          slotProps={{
            htmlInput: {
              "aria-describedby": [nextBad ? "" : id("new-help"), nextBad || sameAsOld ? id("new-err") : ""].filter(Boolean).join(" "),
            },
          }}
          value={next}
          autoComplete="new-password"
          onChange={(e) => setNext(e.target.value)}
        />
      </Field>

      <Field
        label="確認新密碼"
        htmlFor={id("new2")}
        mark="req"
        error={mismatch ? "兩次輸入的密碼不一致，請重新確認。" : null}
        errorId={id("new2-err")}
      >
        <PasswordField
          placeholder="再輸入一次新密碼"
          id={id("new2")}
          fullWidth
          error={mismatch}
          slotProps={{ htmlInput: { "aria-describedby": mismatch ? id("new2-err") : undefined } }}
          value={confirm}
          autoComplete="new-password"
          onChange={(e) => setConfirm(e.target.value)}
          onBlur={() => setTouched(true)}
        />
      </Field>

      <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
        <Button variant="contained" type="submit" aria-disabled={busy} loading={busy} loadingPosition="start">
          {busy ? "變更中…" : "變更密碼"}
        </Button>
        {onCancel ? (
          <Button variant="text" type="button" onClick={onCancel}>
            取消
          </Button>
        ) : null}
      </Stack>
    </Stack>
  );
}

PasswordChangeForm.propTypes = {
  PUT_Password: PropTypes.func,
  onSuccess: PropTypes.func,
  onCancel: PropTypes.func,
  idPrefix: PropTypes.string,
};

PasswordChangeForm.defaultProps = { PUT_Password: () => {}, onSuccess: () => {}, onCancel: null, idPrefix: "pw" };

const mapDispatchToProps = (dispatch) => ({
  PUT_Password: (payload, callback, loading, onError) => dispatch({ type: "PUT_Password", payload, callback, loading, onError }),
});

export default connect(null, mapDispatchToProps)(PasswordChangeForm);
